import { styled, Tooltip, TooltipProps, tooltipClasses } from '@mui/material';
import React from 'react';
import { labelFontSemiBold, vars } from '../../../Theme';

/**
 * Gets the styles for a tooltip instance
 * @param backgroundColor - Background color.
 * @param color - (Optional) Font color. Default is white.
 */
const getStyles = ({ backgroundColor, color = vars.white, ...props }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        ...labelFontSemiBold,
        whiteSpace: 'normal',
        fontSize: 12,
        backgroundColor,
        color,
        ...props,
    },
});

/**
 * Base configurable Tooltip component.
 */
const base = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip
        {...props}
        classes={{ popper: className }}
        enterDelay={500}
        enterNextDelay={500}
        PopperProps={{
            modifiers: [
                {
                    name: 'offset',
                    options: {
                        offset: [0, 0],
                    },
                },
            ],
        }}
    />
));

/** 'Default' colored tooltip (dark background) */
const TooltipDefault = base(() => getStyles({ backgroundColor: vars.font }));

/** 'Primary' colored tooltip. */
const TooltipPrimary = base(() => getStyles({ backgroundColor: vars.brandGreen }));

/** 'White' colored tooltip. */
const TooltipWhite = base(() =>
    getStyles({
        backgroundColor: vars.white,
        boxShadow: '0px 2px 6px 2px #00000026',
        padding: '12px 12px 12px 16px',
        '&.MuiTooltip-tooltip.MuiTooltip-tooltipPlacementTop': {
            marginBottom: '2px',
        },
    }),
);

export type AppTooltipVariant = 'default' | 'primary' | 'white';

/**
 * Interface that describes the props for a customisable tooltip component.
 */
export interface AppTooltipProps extends TooltipProps {
    type?: AppTooltipVariant;
}

/** List of allowed Tooltip placement options. */
export type TooltipPlacement =
    | 'bottom-end'
    | 'bottom-start'
    | 'bottom'
    | 'left-end'
    | 'left-start'
    | 'left'
    | 'right-end'
    | 'right-start'
    | 'right'
    | 'top-end'
    | 'top-start'
    | 'top';

/**
 * Customisable application tooltip.
 */
export default (props: AppTooltipProps) => {
    return props.type === 'primary' ? (
        <TooltipPrimary {...props} />
    ) : props.type === 'white' ? (
        <TooltipWhite {...props} />
    ) : (
        <TooltipDefault {...props} />
    );
};
